import React, { useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X, AlertTriangle, Settings, Clock, Grid as GridIcon, Copy } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import { useMultiGrid } from '../../hooks/useMultiGrid';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { GridType, DepartmentClassConfig } from '../../types/grid';
import EmptyState from '../../components/ui/EmptyState';

interface GridTypeFormData {
  name: string;
  description: string;
  defaultClassMinutes: number;
  linePrefix: string;
  availableLines: string[];
  departmentOverrides: DepartmentClassConfig[];
}

const GridTypeManagement: React.FC = () => {
  const {
    gridTypes,
    allGrids,
    createGridType,
    updateGridType,
    deleteGridType,
    canDeleteGridType
  } = useMultiGrid();
  const { addToast } = useToast();
  const confirm = useConfirm();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGridType, setEditingGridType] = useState<GridType | null>(null);
  const [formData, setFormData] = useState<GridTypeFormData>({
    name: '',
    description: '',
    defaultClassMinutes: 70,
    linePrefix: '',
    availableLines: [],
    departmentOverrides: []
  });

  // Helper function to parse available lines from text input
  const parseAvailableLines = (text: string): string[] => {
    return text
      .split(/[,\s]+/)
      .map(line => line.trim().toUpperCase())
      .filter(line => line.length > 0);
  };

  // Reset form to empty state
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      defaultClassMinutes: 70,
      linePrefix: '',
      availableLines: [],
      departmentOverrides: []
    });
    setEditingGridType(null);
  };

  // Handle create new grid type
  const handleCreate = () => {
    resetForm();
    setShowCreateModal(true);
  };

  // Handle edit existing grid type
  const handleEdit = (gridType: GridType) => {
    setFormData({
      name: gridType.name,
      description: gridType.description || '',
      defaultClassMinutes: gridType.defaultClassMinutes,
      linePrefix: gridType.linePrefix,
      availableLines: gridType.availableLines,
      departmentOverrides: gridType.departmentOverrides || []
    });
    setEditingGridType(gridType);
    setShowCreateModal(true);
  };

  // Handle delete grid type
  const handleDelete = async (gridType: GridType) => {
    if (gridType.isSystemDefault) {
      addToast('System default grid types cannot be deleted', 'warning');
      return;
    }

    if (!canDeleteGridType(gridType.id)) {
      const gridsUsing = allGrids.filter(g => g.gridType?.id === gridType.id);
      addToast(`Cannot delete "${gridType.name}": ${gridsUsing.length} grids are using it`, 'error');
      return;
    }

    if (await confirm({
      title: 'Delete grid type?',
      message: `Are you sure you want to delete "${gridType.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    })) {
      try {
        await deleteGridType(gridType.id);
        addToast(`Grid type "${gridType.name}" deleted successfully`, 'success');
      } catch (error) {
        console.error('Error deleting grid type:', error);
        addToast('Failed to delete grid type', 'error');
      }
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.name.trim() || formData.defaultClassMinutes <= 0) {
      addToast('Please fill in all required fields', 'error');
      return;
    }

    const availableLines = formData.linePrefix 
      ? parseAvailableLines(formData.linePrefix)
      : formData.availableLines;

    if (availableLines.length === 0) {
      addToast('Please specify available lines', 'error');
      return;
    }

    try {
      const gridTypeData: Omit<GridType, 'id'> = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        defaultClassMinutes: formData.defaultClassMinutes,
        linePrefix: availableLines.join(', '),
        availableLines,
        departmentOverrides: formData.departmentOverrides,
        isSystemDefault: false
      };

      if (editingGridType) {
        await updateGridType(editingGridType.id, gridTypeData);
        addToast(`Grid type "${formData.name}" updated successfully`, 'success');
      } else {
        await createGridType(gridTypeData);
        addToast(`Grid type "${formData.name}" created successfully`, 'success');
      }

      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving grid type:', error);
      addToast('Failed to save grid type', 'error');
    }
  };

  // Get usage count for a grid type
  const getUsageCount = (gridTypeId: string) => {
    return allGrids.filter(grid => grid.gridType?.id === gridTypeId).length;
  };

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Grid Type Management" 
        subtitle="Configure grid types that define class durations and available lines"
        icon={<Settings className="h-8 w-8" />}
        action={
          <Button 
            onClick={handleCreate}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Create Grid Type
          </Button>
        }
      />

      {/* Grid Types List */}
      <div className="grid gap-4">
        {gridTypes.map(gridType => {
          const usageCount = getUsageCount(gridType.id);
          const canDelete = canDeleteGridType(gridType.id) && !gridType.isSystemDefault;

          return (
            <Card key={gridType.id} className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <GridIcon className="h-5 w-5 text-navy-600" />
                    <h3 className="text-card-title">{gridType.name}</h3>
                    {gridType.isSystemDefault && (
                      <span className="px-2 py-1 bg-ink-100 text-ink-500 text-xs rounded">
                        System Default
                      </span>
                    )}
                  </div>

                  {gridType.description && (
                    <p className="text-ink-500 mb-3">{gridType.description}</p>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-ink-400" />
                      <span className="font-mono tabular-nums font-medium">{gridType.defaultClassMinutes} minutes</span>
                      <span className="text-ink-500">per class</span>
                    </div>

                    <div>
                      <span className="text-ink-500">Lines:</span>
                      <span className="ml-2 font-mono tabular-nums text-navy-600">
                        {gridType.availableLines.join(', ')}
                      </span>
                    </div>

                    <div>
                      <span className="text-ink-500">Used by:</span>
                      <span className="ml-2 font-mono tabular-nums font-medium">
                        {usageCount} grid{usageCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {gridType.departmentOverrides && gridType.departmentOverrides.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <span className="text-sm text-ink-500">Learning Area Overrides:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {gridType.departmentOverrides.map(override => (
                          <span 
                            key={override.departmentId}
                            className="px-2 py-1 bg-navy-50 text-navy-700 text-xs rounded"
                          >
                            {override.departmentName}: {override.classMinutes}min
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 ml-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleEdit(gridType)}
                    leftIcon={<Edit2 className="h-4 w-4" />}
                  >
                    Edit
                  </Button>
                  
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleDelete(gridType)}
                    disabled={!canDelete}
                    leftIcon={<Trash2 className="h-4 w-4" />}
                    className={!canDelete ? 'opacity-50 cursor-not-allowed' : ''}
                    title={
                      !canDelete 
                        ? gridType.isSystemDefault 
                          ? 'System defaults cannot be deleted'
                          : `Cannot delete: ${usageCount} grids are using this type`
                        : 'Delete this grid type'
                    }
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}

        {gridTypes.length === 0 && (
          <Card>
            <EmptyState
              icon={<GridIcon className="h-6 w-6" />}
              title="No Grid Types"
              description="Create your first grid type to define class durations and available lines."
              action={<Button onClick={handleCreate} leftIcon={<Plus className="h-4 w-4" />}>
                Create Grid Type
              </Button>}
            />
          </Card>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title={editingGridType ? `Edit ${editingGridType.name}` : 'Create New Grid Type'}
        size="lg"
        footer={
          <div className="flex gap-2">
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit}
              leftIcon={<Save className="h-4 w-4" />}
            >
              {editingGridType ? 'Update' : 'Create'} Grid Type
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500"
              placeholder="e.g., Senior Main Grid, Junior PE Classes"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500"
              placeholder="Describe when this grid type should be used..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                Class Duration (minutes) *
              </label>
              <input
                type="number"
                min="1"
                max="300"
                value={formData.defaultClassMinutes}
                onChange={(e) => setFormData({ ...formData, defaultClassMinutes: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500"
                placeholder="70"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                Available Lines *
              </label>
              <input
                type="text"
                value={formData.linePrefix}
                onChange={(e) => {
                  const lines = parseAvailableLines(e.target.value);
                  setFormData({ 
                    ...formData, 
                    linePrefix: e.target.value,
                    availableLines: lines
                  });
                }}
                className="w-full px-3 py-2 border border-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500"
                placeholder="A, B, C, D, E, F"
              />
              <p className="text-xs text-ink-500 mt-1">
                Separate lines with commas. Preview: {formData.linePrefix ? parseAvailableLines(formData.linePrefix).join(', ') : 'None'}
              </p>
            </div>
          </div>

          {editingGridType?.isSystemDefault && (
            <div className="bg-warn-bg border border-warn-border rounded-md p-3">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-warn-fg mr-2 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-warn-fg">System Default Grid Type</p>
                  <p className="text-warn-fg mt-1">
                    Changes to system defaults will affect all schools using this system. 
                    Consider creating a custom grid type instead.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default GridTypeManagement;